import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  StudyProgrammeEditPageData,
  StudyProgrammeEditPageDataVariables,
  UpdateStudyProgramme,
  UpdateStudyProgrammeVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import {
  extractNullableMarkdownContent,
  markdownInput,
} from "common/input-types";
import { urls } from "common/urls";
import { adminStudyProgrammeFragment } from "domain/admin/fragments";
import gql from "graphql-tag";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

const GET_STUDY_PROGRAMME = gql`
  query StudyProgrammeEditPageData($studyProgrammeId: ID!) {
    studyProgrammeById(id: $studyProgrammeId) {
      ...AdminStudyProgrammeFragment
    }
  }
  ${adminStudyProgrammeFragment}
`;

const MUTATION = gql`
  mutation UpdateStudyProgramme($input: UpdateStudyProgrammeDetailsInput!) {
    updateStudyProgrammeDetails(input: $input) {
      studyProgramme {
        ...AdminStudyProgrammeFragment
      }
    }
  }
  ${adminStudyProgrammeFragment}
`;

interface FormValues {
  status: ContentStatus;
  title: string;
  description: string;
  folders?: { value: string; label: string }[];
}

type RouteProps = {
  studyProgrammeId: string;
};

export const StudyProgrammeEditPage: React.FC = () => {
  const { studyProgrammeId } = useParams<RouteProps>();
  const methods = useForm<FormValues>();
  const [contentStatus, setContentStatus] = React.useState<ContentStatus>();

  if (!studyProgrammeId) {
    throw new Error("Unexpected error: Missing studyProgrammeId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    StudyProgrammeEditPageData,
    StudyProgrammeEditPageDataVariables
  >(GET_STUDY_PROGRAMME, {
    variables: {
      studyProgrammeId,
    },
  });

  const [mutate, { error, loading }] = useMutation<
    UpdateStudyProgramme,
    UpdateStudyProgrammeVariables
  >(MUTATION);

  useEffect(() => {
    if (data) {
      setContentStatus(data.studyProgrammeById.status);
    }
  }, [data]);

  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  function exitEditPage() {
    if (previousPath && !previousPath.endsWith("/administration")) {
      navigate(-1);
    } else {
      navigate(urls.admin);
    }
  }

  const onSubmit = async (content: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: studyProgrammeId,
          status: { updatedValue: contentStatus },
          title: { updatedValue: content.title },
          description: {
            updatedValue: markdownInput(content.description),
          },
          folderIds: content.folders?.map((it) => it.value),
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  return (
    <AdministrationLayout title={data.studyProgrammeById.title}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Navn på studieprogram"
            fieldProps={methods.register("title", {
              required: "Du må gi studieprogrammet en tittel",
            })}
            defaultValue={data.studyProgrammeById.title}
          />

          {contentStatus && (
            <Select
              name="status"
              title="Status"
              options={contentStatusOptions()}
              value={contentStatus.toString()}
              onChange={(newValue: string) => {
                setContentStatus(newValue as ContentStatus);
              }}
            />
          )}

          <MarkdownField<FormValues>
            name="description"
            title="Beskrivelse av studieprogram"
            defaultValue={
              extractNullableMarkdownContent(
                data.studyProgrammeById.description,
              ) ?? ""
            }
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            submitText="Lagre"
            isLoading={loading}
          />
        </form>
      </FormProvider>
    </AdministrationLayout>
  );
};
